// SA_Overview1.jsx (updated with filters on same row as title)
import React, { useState, useEffect, useRef, useMemo } from 'react';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useModalEscape from '../../hooks/useModalEscape';
import useModalTransition from '../../hooks/useModalTransition';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import Sidebar from '../../components/Sidebar/Sidebar';
import './SA_Overview1.css';
import Chart from 'chart.js/auto';
import {
  CHART_PRIMARY_START,
  CHART_PRIMARY_END,
  createPrimaryBarGradient,
} from '../../utils/chartBrandGradient';

// Import lucide-static icons
import { 
  Users, 
  Shield,
  Server,
  File,
  UserCheck,
  Database,
  X,
  MoreVertical
} from 'lucide-static';
import { } from '../../services/documentService';
import DocumentUploadCalendar from '../../components/DocumentUploadCalendar';



// Icon wrapper component
const Icon = ({ icon, className, size = 20, color, ...props }) => {
  const getProcessedSvg = () => {
    let svgString = icon;
    if (size) svgString = svgString.replace('<svg', `<svg width="${size}" height="${size}"`);
    if (color) svgString = svgString.replace('<svg', `<svg color="${color}" stroke="${color}"`);
    if (className) svgString = svgString.replace('<svg', `<svg class="${className}"`);
    return svgString;
  };
  return <span dangerouslySetInnerHTML={{ __html: getProcessedSvg() }} {...props} />;
};

// Helper functions moved outside component to remain stable
const getStatusColor = (type, value) => {
  const GOOD = '#10B981';    // Green
  const MID = '#FACC15';     // Yellow
  const KINDA_BAD = '#F97316'; // Orange
  const BAD = '#EF4444';      // Red
  const DEFAULT = CHART_PRIMARY_START;

  if (type === 'security') {
    const val = parseInt(value) || 0;
    if (val === 0) return GOOD;
    if (val <= 2) return MID;
    if (val <= 5) return KINDA_BAD;
    return BAD;
  }

  if (type === 'percentage') {
    const val = typeof value === 'string' ? parseFloat(value.replace('%', '')) : parseFloat(value);
    if (isNaN(val)) return DEFAULT;
    if (val >= 99.0) return GOOD;
    if (val >= 95.0) return MID;
    if (val >= 90.0) return KINDA_BAD;
    return BAD;
  }

  return DEFAULT;
};

const getTimeAgo = (ms) => {
  if (!ms || typeof ms !== 'number') return 'Unknown';
  const diff = Date.now() - ms;
  if (diff < 60 * 1000) return 'Just now';
  const mins = Math.floor(diff / (60 * 1000));
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(diff / (60 * 60 * 1000));
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days < 30) return `${days} days ago`;
  return new Date(ms).toLocaleDateString();
};

const sortAndLimitRecentUsers = (usersList) => {
  return [...(usersList || [])]
    .sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0))
    .slice(0, 3);
};

const SA_Overview1 = () => {
  useDocumentTitle("Avisa Admin Dashboard");

  const { user, getIdToken } = useAuth();


  // Chart refs
  const ordinanceChartRef = useRef(null);
  const ordinanceChartInstanceRef = useRef(null);
  
  const userGrowthChartRef = useRef(null);
  const userGrowthChartInstanceRef = useRef(null);

  // Pie chart refs for user location distribution
  const userLocationChartRef = useRef(null);
  const userLocationChartInstanceRef = useRef(null);

  // Top Contributors chart refs (doughnut chart)
  const topContributorsChartRef = useRef(null);
  const topContributorsChartInstanceRef = useRef(null);
  
  // Data from global context
  const { allDocuments, stats, users, statsLoaded, documentsLoaded } = useData();

  // loading state (only for very first load or if no data yet)
  
  // Independent filters per chart
  const [ordinanceActivityFilter, setOrdinanceActivityFilter] = useState('both');
  const [ordinanceLevelFilter, setOrdinanceLevelFilter] = useState('all');
  const [ordinanceProvinceFilter, setOrdinanceProvinceFilter] = useState('all');
  const [ordinanceMunicipalityFilter, setOrdinanceMunicipalityFilter] = useState('all');
  const [ordinanceBarangayFilter, setOrdinanceBarangayFilter] = useState('all');
  const [showOrdinanceFilters, setShowOrdinanceFilters] = useState(false);
  const [tempOrdinanceFilters, setTempOrdinanceFilters] = useState({ activity: 'both', level: 'all', province: 'all', municipality: 'all', barangay: 'all' });
  
  // Independent filters for User Growth Chart
  const [userGrowthRange, setUserGrowthRange] = useState('30');
  const [showUserGrowthFilters, setShowUserGrowthFilters] = useState(false);
  const [tempUserGrowthRange, setTempUserGrowthRange] = useState('30');

  // Filter for user location chart
  const [userLocationFilter, setUserLocationFilter] = useState('province');
  const [userProvinceFilter, setUserProvinceFilter] = useState('all');
  const [userMunicipalityFilter, setUserMunicipalityFilter] = useState('all');
  const [userBarangayFilter, setUserBarangayFilter] = useState('all');
  const [showUserLocationFilters, setShowUserLocationFilters] = useState(false);
  const [tempUserLocationFilters, setTempUserLocationFilters] = useState({ 
    groupBy: 'province', 
    province: 'all', 
    municipality: 'all', 
    barangay: 'all' 
  });

  // Filter for top contributors (by legal level: all, provincial, municipal, barangay)
  const [topContributorsFilter, setTopContributorsFilter] = useState('all');
  const [topContributorsProvinceFilter, setTopContributorsProvinceFilter] = useState('all');
  const [topContributorsMunicipalityFilter, setTopContributorsMunicipalityFilter] = useState('all');
  const [topContributorsBarangayFilter, setTopContributorsBarangayFilter] = useState('all');
  const [tempTopContributorsFilters, setTempTopContributorsFilters] = useState({ level: 'all', province: 'all', municipality: 'all', barangay: 'all' });
  const [showTopContributorsFilters, setShowTopContributorsFilters] = useState(false);

  // State for Full View Modal - Most Consulted Documents
  const [showConsultedDocsModal, setShowConsultedDocsModal] = useState(false);
  const renderConsultedDocsModal = useModalTransition(showConsultedDocsModal);
  const [consultedDocsSortBy, setConsultedDocsSortBy] = useState('consultations'); // 'consultations', 'title'
  const [consultedDocsSortOrder, setConsultedDocsSortOrder] = useState('desc'); // 'asc', 'desc'
  const [consultedDocsSearchQuery, setConsultedDocsSearchQuery] = useState('');

  const closeAllFilters = () => {
    setShowOrdinanceFilters(false);
    setShowUserGrowthFilters(false);
    setShowUserLocationFilters(false);
    setShowTopContributorsFilters(false);
    setShowConsultedDocsModal(false);
  };

  useModalEscape(closeAllFilters, showOrdinanceFilters || showUserGrowthFilters || showUserLocationFilters || showTopContributorsFilters || showConsultedDocsModal);

  const sortAndLimitRecentUsers = (usersList) => {
    return [...(usersList || [])]
      .sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0))
      .slice(0, 3);
  };

  // Track user activity
  const trackUserActivity = async () => {
    try {
      if (!user) return;
      const token = await getIdToken();
      if (!token) return;
      await fetch('https://avisa-huja.onrender.com/api/analytics/track-activity', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (err) {
      console.error('Error tracking activity:', err);
    }
  };

  useEffect(() => {
    if (user) {
      const { adminaccessLevel, location, role } = user;
      const isSysAdmin = role === 'system_admin';
      
      if (!isSysAdmin) {
        if (adminaccessLevel === 'municipal') {
          setOrdinanceLevelFilter('all');
          setOrdinanceProvinceFilter('all');
          setOrdinanceMunicipalityFilter(location?.municipality || 'all');
        } else if (adminaccessLevel === 'barangay') {
          setOrdinanceLevelFilter('barangay');
          setOrdinanceProvinceFilter(location?.province || 'all');
          setOrdinanceMunicipalityFilter(location?.municipality || 'all');
          setOrdinanceBarangayFilter(location?.barangay || 'all');
        }
      }
    }
  }, [user]);

  useEffect(() => {
    if (user) trackUserActivity();
    
    // Only show loading if we haven't loaded data yet
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, statsLoaded, documentsLoaded]);

  // Derived available barangays for Ordinance Chart (dependent on ordinance municipality filter)
  const availableOrdinanceBarangays = React.useMemo(() => {
    const unique = new Set();
    let docs = allDocuments;
    
    if (ordinanceMunicipalityFilter !== 'all') {
      docs = docs.filter(d => {
        const docMuni = String(d.location?.municipality || d.municipality || "").trim();
        return docMuni.toLowerCase() === ordinanceMunicipalityFilter.toLowerCase();
      });
    }

    docs.forEach((doc) => {
      const brgy = String(doc.location?.barangay || doc.barangay || "").trim();
      if (brgy) unique.add(brgy);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [allDocuments, ordinanceMunicipalityFilter]);

  // Derived available barangays for Token Chart (dependent on token municipality filter)

  // Derived available provinces from documents
  const availableProvinces = React.useMemo(() => {
    const unique = new Set();
    allDocuments.forEach((doc) => {
      const prov = String(doc.location?.province || doc.province || "").trim();
      if (prov) unique.add(prov);
    });
    users.forEach((u) => {
      const prov = String(u.location?.province || "").trim();
      if (prov) unique.add(prov);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [allDocuments, users]);

  // Derived available municipalities from users (dependent on selected province)
  const availableUserMunicipalities = React.useMemo(() => {
    const unique = new Set();
    const provToMatch = (showUserLocationFilters ? tempUserLocationFilters.province : userProvinceFilter);
    
    let filteredUsers = users;
    if (provToMatch !== 'all') {
      filteredUsers = filteredUsers.filter(u => {
        const prov = String(u.location?.province || "").trim();
        return prov.toLowerCase() === provToMatch.toLowerCase();
      });
    }
    filteredUsers.forEach((u) => {
      const muni = String(u.location?.municipality || "").trim();
      if (muni) unique.add(muni);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [users, userProvinceFilter, tempUserLocationFilters.province, showUserLocationFilters]);

  // Derived available barangays from users (dependent on selected municipality)
  const availableUserBarangays = React.useMemo(() => {
    const unique = new Set();
    const muniToMatch = (showUserLocationFilters ? tempUserLocationFilters.municipality : userMunicipalityFilter);

    let filteredUsers = users;
    if (muniToMatch !== 'all') {
      filteredUsers = filteredUsers.filter(u => {
        const muni = String(u.location?.municipality || "").trim();
        return muni.toLowerCase() === muniToMatch.toLowerCase();
      });
    }
    filteredUsers.forEach((u) => {
      const brgy = String(u.location?.barangay || "").trim();
      if (brgy) unique.add(brgy);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [users, userMunicipalityFilter, tempUserLocationFilters.municipality, showUserLocationFilters]);

  // Derived available municipalities from documents (dependent on province)
  const availableOrdinanceMunicipalities = React.useMemo(() => {
    const unique = new Set();
    let docs = allDocuments;
    if (ordinanceProvinceFilter !== 'all') {
      docs = docs.filter(d => {
        const prov = String(d.location?.province || d.province || "").trim();
        return prov.toLowerCase() === ordinanceProvinceFilter.toLowerCase();
      });
    }
    docs.forEach((doc) => {
      const muni = String(doc.location?.municipality || doc.municipality || "").trim();
      if (muni) unique.add(muni);
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [allDocuments, ordinanceProvinceFilter]);


  // Derived top ordinances from documents
  const topOrdinances = React.useMemo(() => {
    let docs = [...allDocuments];
    
    // Filter by legal level
    if (ordinanceLevelFilter && ordinanceLevelFilter.toLowerCase() !== 'all') {
      docs = docs.filter(d => {
        const level = String(d.legalLevel || "").trim().toLowerCase();
        return level === ordinanceLevelFilter.toLowerCase();
      });
    }

    // Filter by province
    if (ordinanceProvinceFilter && ordinanceProvinceFilter.toLowerCase() !== 'all') {
      docs = docs.filter(d => {
        const prov = String(d.location?.province || d.province || "").trim();
        return prov.toLowerCase() === ordinanceProvinceFilter.toLowerCase();
      });
    }

    // Filter by municipality
    if (ordinanceMunicipalityFilter && ordinanceMunicipalityFilter.toLowerCase() !== 'all') {
      docs = docs.filter(d => {
        const docMuni = String(d.location?.municipality || d.municipality || "").trim();
        return docMuni.toLowerCase() === ordinanceMunicipalityFilter.toLowerCase();
      });
    }

    // Filter by barangay
    if (ordinanceBarangayFilter && ordinanceBarangayFilter.toLowerCase() !== 'all') {
      docs = docs.filter(d => {
        const docBrgy = String(d.location?.barangay || d.barangay || "").trim();
        return docBrgy.toLowerCase() === ordinanceBarangayFilter.toLowerCase();
      });
    }

    // Sort by consultation count (sum of analysis and generation)
    return docs
      .map(d => ({
        title: d.simplifiedTitle || d.title || 'Untitled Ordinance',
        consultations: (d.consultation?.analysisConsultation || 0) + (d.consultation?.generationConsultation || 0)
      }))
      .sort((a, b) => b.consultations - a.consultations)
      .slice(0, 6);
  }, [allDocuments, ordinanceLevelFilter, ordinanceProvinceFilter, ordinanceMunicipalityFilter, ordinanceBarangayFilter]);

  // Derived total consultations
  const totalConsultations = React.useMemo(() => {
    return allDocuments.reduce((sum, d) => {
      return sum + (d.consultation?.analysisConsultation || 0) + (d.consultation?.generationConsultation || 0);
    }, 0);
  }, [allDocuments]);

  // Derived all consulted documents (full list, not limited to 6)
  const allConsultedDocuments = React.useMemo(() => {
    let docs = [...allDocuments];

    // Filter by level (activity)
    if (ordinanceActivityFilter !== 'both') {
      // Note: activity filter is for analysis vs generation, implement if needed
    }

    // Filter by level (law level)
    if (ordinanceLevelFilter && ordinanceLevelFilter.toLowerCase() !== 'all') {
      docs = docs.filter(d => {
        const docLevel = String(d.legalLevel || d.level || "").trim().toLowerCase();
        return docLevel === ordinanceLevelFilter.toLowerCase();
      });
    }

    // Filter by province
    if (ordinanceProvinceFilter && ordinanceProvinceFilter.toLowerCase() !== 'all') {
      docs = docs.filter(d => {
        const prov = String(d.location?.province || d.province || "").trim();
        return prov.toLowerCase() === ordinanceProvinceFilter.toLowerCase();
      });
    }

    // Filter by municipality
    if (ordinanceMunicipalityFilter && ordinanceMunicipalityFilter.toLowerCase() !== 'all') {
      docs = docs.filter(d => {
        const docMuni = String(d.location?.municipality || d.municipality || "").trim();
        return docMuni.toLowerCase() === ordinanceMunicipalityFilter.toLowerCase();
      });
    }

    // Filter by barangay
    if (ordinanceBarangayFilter && ordinanceBarangayFilter.toLowerCase() !== 'all') {
      docs = docs.filter(d => {
        const docBrgy = String(d.location?.barangay || d.barangay || "").trim();
        return docBrgy.toLowerCase() === ordinanceBarangayFilter.toLowerCase();
      });
    }

    // Map to include consultation data
    const mapped = docs.map(d => ({
      id: d.id,
      title: d.simplifiedTitle || d.title || 'Untitled Ordinance',
      consultations: (d.consultation?.analysisConsultation || 0) + (d.consultation?.generationConsultation || 0),
      analysisConsultation: d.consultation?.analysisConsultation || 0,
      generationConsultation: d.consultation?.generationConsultation || 0,
      legalLevel: d.legalLevel || d.level || 'Unknown',
      province: d.location?.province || d.province || 'Unknown',
      municipality: d.location?.municipality || d.municipality || 'Unknown',
      barangay: d.location?.barangay || d.barangay || 'Unknown',
      year: d.year || 'Unknown',
      originalDocument: d
    }));

    // Sort based on current sort preferences
    return mapped.sort((a, b) => {
      if (consultedDocsSortBy === 'title') {
        const aTitle = a.title.toLowerCase();
        const bTitle = b.title.toLowerCase();
        return consultedDocsSortOrder === 'asc' ? aTitle.localeCompare(bTitle) : bTitle.localeCompare(aTitle);
      } else {
        // Sort by consultations
        const diff = b.consultations - a.consultations;
        return consultedDocsSortOrder === 'asc' ? -diff : diff;
      }
    });
  }, [allDocuments, ordinanceLevelFilter, ordinanceProvinceFilter, ordinanceMunicipalityFilter, ordinanceBarangayFilter, consultedDocsSortBy, consultedDocsSortOrder]);

  // Filter consulted documents by search query
  const filteredConsultedDocuments = React.useMemo(() => {
    if (!consultedDocsSearchQuery.trim()) {
      return allConsultedDocuments;
    }

    const query = consultedDocsSearchQuery.toLowerCase();
    return allConsultedDocuments.filter(doc => 
      doc.title.toLowerCase().includes(query) ||
      doc.legalLevel.toLowerCase().includes(query) ||
      doc.province.toLowerCase().includes(query) ||
      doc.municipality.toLowerCase().includes(query)
    );
  }, [allConsultedDocuments, consultedDocsSearchQuery]);

  // Derived User Growth Data
  const userGrowthData = React.useMemo(() => {
    if (!users || users.length === 0) return { labels: [], dataset: [] };

    // Get range based on selection
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    
    let start = new Date();
    let days = 30;

    if (userGrowthRange === 'overall') {
      const earliestUser = [...users].sort((a,b) => (a.createdAtMs || 0) - (b.createdAtMs || 0))[0];
      if (earliestUser?.createdAtMs) {
        start = new Date(earliestUser.createdAtMs);
      } else {
        start.setDate(start.getDate() - 30);
      }
      start.setHours(0, 0, 0, 0);
      days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    } else {
      days = parseInt(userGrowthRange) || 30;
      start.setDate(start.getDate() - days);
      start.setHours(0, 0, 0, 0);
    }

    // Create daily map
    const dailyMap = {};
    for (let i = 0; i <= days; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const key = d.toISOString().split('T')[0];
      dailyMap[key] = 0;
    }

    // Count registrations per day
    users.forEach(u => {
      if (u.createdAtMs) {
        const d = new Date(u.createdAtMs);
        if (d >= start && d <= end) {
          const key = d.toISOString().split('T')[0];
          if (dailyMap[key] !== undefined) dailyMap[key]++;
        }
      }
    });

    // Count total users before the start of the 30-day window
    let cumulativeTotal = users.filter(u => u.createdAtMs && u.createdAtMs < start.getTime()).length;

    const labels = [];
    const dataset = [];

    // Formulate labels and cumulative data
    Object.keys(dailyMap).sort().forEach(date => {
      cumulativeTotal += dailyMap[date];
      const d = new Date(date);
      labels.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      dataset.push(cumulativeTotal);
    });

    return { labels, dataset };
  }, [users, userGrowthRange]);

  // Derived top contributors data for doughnut chart - grouped by legalLevel
  const topContributorsData = React.useMemo(() => {
    if (!allDocuments || allDocuments.length === 0) return { labels: [], data: [], colors: [], total: 0 };
    
    const contributorCounts = {};
    
    allDocuments.forEach(doc => {
      const legalLevel = String(doc.legalLevel || '').trim();
      let contributorKey = 'Unknown';
      
      // Base the contributor name on the document's legalLevel
      if (legalLevel === 'Provincial') {
        contributorKey = doc.location?.province || doc.province || 'Unknown Province';
      } else if (legalLevel === 'Municipal') {
        const muni = doc.location?.municipality || doc.municipality || '';
        const prov = doc.location?.province || doc.province || '';
        contributorKey = muni ? (prov ? `${muni}, ${prov}` : muni) : 'Unknown Municipality';
      } else if (legalLevel === 'Barangay') {
        const brgy = doc.location?.barangay || doc.barangay || '';
        const muni = doc.location?.municipality || doc.municipality || '';
        contributorKey = brgy ? (muni ? `${brgy}, ${muni}` : brgy) : 'Unknown Barangay';
      } else {
        // National or other levels - use legalLevel as key
        contributorKey = legalLevel || 'Other';
      }
      
      // Filter by selected level if not 'all'
      if (topContributorsFilter !== 'all') {
        const filterLevel = topContributorsFilter.charAt(0).toUpperCase() + topContributorsFilter.slice(1);
        if (legalLevel !== filterLevel) return; // Skip documents that don't match the filter
      }

      // Filter by selected location
      if (topContributorsProvinceFilter !== 'all' && String(doc.location?.province || doc.province || '').toLowerCase() !== topContributorsProvinceFilter.toLowerCase()) return;
      if (topContributorsMunicipalityFilter !== 'all' && String(doc.location?.municipality || doc.municipality || '').toLowerCase() !== topContributorsMunicipalityFilter.toLowerCase()) return;
      if (topContributorsBarangayFilter !== 'all' && String(doc.location?.barangay || doc.barangay || '').toLowerCase() !== topContributorsBarangayFilter.toLowerCase()) return;
      
      contributorCounts[contributorKey] = (contributorCounts[contributorKey] || 0) + 1;
    });
    
    const sorted = Object.entries(contributorCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
    
    const colorPalette = [
      '#9fa3c7', '#6c72a8', '#31356e', '#1c1f4e',
      '#7a8199', '#4a4f7a', '#252846', '#5b6390'
    ];
    
    const dataArr = sorted.map(s => s.count);
    const total = dataArr.reduce((sum, val) => sum + val, 0);
    
    return {
      labels: sorted.map(s => s.name),
      data: dataArr,
      colors: sorted.map((_, i) => colorPalette[i % colorPalette.length]),
      total
    };
  }, [allDocuments, topContributorsFilter, topContributorsProvinceFilter, topContributorsMunicipalityFilter, topContributorsBarangayFilter]);

  // Derived user location data for bar chart
  const userLocationData = React.useMemo(() => {
    if (!users || users.length === 0) return { labels: [], data: [] };

    const locationUserCounts = {};
    
    // Filter users first
    let filteredUsers = users;
    if (userProvinceFilter !== 'all') {
      filteredUsers = filteredUsers.filter(u => String(u.location?.province || "").trim().toLowerCase() === userProvinceFilter.toLowerCase());
    }
    if (userMunicipalityFilter !== 'all') {
      filteredUsers = filteredUsers.filter(u => String(u.location?.municipality || "").trim().toLowerCase() === userMunicipalityFilter.toLowerCase());
    }
    if (userBarangayFilter !== 'all') {
      filteredUsers = filteredUsers.filter(u => String(u.location?.barangay || "").trim().toLowerCase() === userBarangayFilter.toLowerCase());
    }

    filteredUsers.forEach(u => {
      let locationKey = 'Unknown';
      if (userLocationFilter === 'province') {
        locationKey = u.location?.province || 'Unknown';
      } else if (userLocationFilter === 'municipality') {
        const prov = u.location?.province || '';
        const muni = u.location?.municipality || '';
        locationKey = muni ? (prov ? `${muni}, ${prov}` : muni) : 'Unknown';
      } else if (userLocationFilter === 'barangay') {
        const brgy = u.location?.barangay || '';
        const muni = u.location?.municipality || '';
        locationKey = brgy ? (muni ? `${brgy}, ${muni}` : brgy) : 'Unknown';
      }
      locationUserCounts[locationKey] = (locationUserCounts[locationKey] || 0) + 1;
    });

    const sorted = Object.entries(locationUserCounts)
      .map(([location, count]) => ({ location, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    return {
      labels: sorted.map(s => s.location),
      data: sorted.map(s => s.count),
      total: filteredUsers.length
    };
  }, [users, userLocationFilter, userProvinceFilter, userMunicipalityFilter, userBarangayFilter]);

  // Initialize Ordinance Chart (BAR CHART)
  useEffect(() => {
    let isMounted = true;
    
    const loadOrdinanceChart = () => {
      if (!isMounted || !ordinanceChartRef.current) return;
      
      const ctx = ordinanceChartRef.current.getContext('2d');
      if (!ctx) return;
      
      if (ordinanceChartInstanceRef.current) ordinanceChartInstanceRef.current.destroy();
      
      let labels = topOrdinances.map(o => o.title);
      let dataValues = topOrdinances.map(o => o.consultations);

      // If no data, show dummy with a message (or just empty)
      if (labels.length === 0) {
        labels = ['No Data Available'];
        dataValues = [0];
      }
      
      if (ordinanceActivityFilter === 'generation') {
        dataValues = topOrdinances.map(o => (allDocuments.find(d => (d.simplifiedTitle || d.title) === o.title)?.consultation?.generationConsultation || 0));
      } else if (ordinanceActivityFilter === 'analysis') {
        dataValues = topOrdinances.map(o => (allDocuments.find(d => (d.simplifiedTitle || d.title) === o.title)?.consultation?.analysisConsultation || 0));
      }
      
      ordinanceChartInstanceRef.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: 'Consultations',
            data: dataValues.map(v => Math.max(0, v)),
            backgroundColor: (context) =>
              createPrimaryBarGradient(context.chart.ctx, context.chart.chartArea),
            hoverBackgroundColor: CHART_PRIMARY_END,
            borderColor: CHART_PRIMARY_START,
            borderWidth: 0,
            borderRadius: 6,
            barPercentage: 0.7,
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 1200,
            easing: 'easeOutQuart',
            delay: (context) => {
              let delay = 0;
              if (context.type === 'data' && context.mode === 'default' && !context.chart.alreadyAnimated) {
                delay = context.dataIndex * 100;
              }
              return delay;
            },
            onComplete: (context) => {
              context.chart.alreadyAnimated = true;
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(28, 31, 78, 0.94)',
              displayColors: false,
              titleColor: '#FFFFFF',
              bodyColor: '#FFFFFF',
              borderColor: 'rgba(255, 255, 255, 0.12)',
              borderWidth: 1,
              padding: 8,
              cornerRadius: 6,
            },
          },
          scales: {
            x: { beginAtZero: true, grid: { color: '#E5E7EB' }, ticks: { maxTicksLimit: 10, callback: function(val, index, ticks) {
              const rounded = Math.round(val);
              if (index === 0) return rounded.toLocaleString();
              const prevRounded = Math.round(ticks[index - 1].value);
              if (rounded === prevRounded) return '';
              return rounded.toLocaleString();
            }} },
            y: { grid: { display: false } }
          }
        }
      });
    };
    
    loadOrdinanceChart();
    return () => { isMounted = false; if (ordinanceChartInstanceRef.current) ordinanceChartInstanceRef.current.destroy(); };
  }, [ordinanceActivityFilter, ordinanceLevelFilter, ordinanceProvinceFilter, ordinanceMunicipalityFilter, ordinanceBarangayFilter, topOrdinances]);

  // Initialize User Growth Chart (LINE CHART)
  useEffect(() => {
    let isMounted = true;

    const loadUserGrowthChart = () => {
      if (!isMounted || !userGrowthChartRef.current) return;

      const ctx = userGrowthChartRef.current.getContext('2d');
      if (!ctx) return;

      if (userGrowthChartInstanceRef.current) userGrowthChartInstanceRef.current.destroy();

      const { labels, dataset } = userGrowthData;

      if (labels.length === 0) return;

      const gradient = ctx.createLinearGradient(0, 0, 0, 300);
      gradient.addColorStop(0, 'rgba(124, 58, 237, 0.2)');
      gradient.addColorStop(1, 'rgba(124, 58, 237, 0)');

      userGrowthChartInstanceRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            label: 'Total Users',
            data: dataset,
            fill: true,
            backgroundColor: gradient,
            borderColor: CHART_PRIMARY_START,
            borderWidth: 2.5,
            tension: 0.45,
            pointRadius: 0,
            pointHoverRadius: 0,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(28, 31, 78, 0.94)',
              padding: 10,
              cornerRadius: 6,
              displayColors: false,
              callbacks: {
                label: (context) => `Total Users: ${context.parsed.y.toLocaleString()}`
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              border: { display: false },
              ticks: {
                font: { size: 11 },
                color: '#94a3b8',
                maxRotation: 0,
                autoSkip: true,
                maxTicksLimit: 7,
              }
            },
            y: {
              beginAtZero: false,
              grid: { color: 'rgba(0, 0, 0, 0.04)', drawBorder: false },
              border: { display: false },
              ticks: {
                font: { size: 11 },
                color: '#94a3b8',
                padding: 8,
                stepSize: 1,
                major: { enabled: true },
                callback: (val) => Math.round(val).toLocaleString()
              }
            }
          }
        }
      });
    };

    loadUserGrowthChart();
    return () => { isMounted = false; if (userGrowthChartInstanceRef.current) userGrowthChartInstanceRef.current.destroy(); };
  }, [userGrowthData, userGrowthRange]);

  const statistics = useMemo(() => [
    { 
      title: 'Total Users', 
      value: statsLoaded ? (stats.totalUsers || 0).toLocaleString() : '0', 
      changePercent: '', 
      changeText: 'Registered community accounts', 
      icon: Users, 
      valueColor: CHART_PRIMARY_START, 
      changeColor: CHART_PRIMARY_START 
    },
    { 
      title: 'Active Users', 
      value: statsLoaded ? (stats.activeUsers || 0).toLocaleString() : '0', 
      changePercent: '', 
      changeText: 'Interactive member sessions', 
      icon: UserCheck, 
      valueColor: CHART_PRIMARY_START, 
      changeColor: CHART_PRIMARY_START 
    },
    { 
      title: 'Content Items', 
      value: statsLoaded ? (stats.totalDocuments || 0).toLocaleString() : '0', 
      changePercent: '', 
      changeText: 'Verified legal document entries', 
      icon: File, 
      valueColor: CHART_PRIMARY_START, 
      changeColor: CHART_PRIMARY_START 
    },
    { 
      title: 'Security Alert', 
      value: statsLoaded ? (stats.securityAlerts || 0).toString() : '0', 
      changePercent: '', 
      changeText: (stats.securityAlerts || 0) > 0 ? 'Urgent security review required' : 'Continuous security monitoring', 
      icon: Shield, 
      valueColor: getStatusColor('security', stats.securityAlerts), 
      changeColor: getStatusColor('security', stats.securityAlerts) 
    },
    {
      title: 'Server Uptime',
      value: statsLoaded ? (stats.serverUptime || stats.uptime || '0%') : '0%',
      changePercent: '',
      changeText: (() => {
        if (!statsLoaded) return 'Checking...';
        const isOnline = stats.serverStatus === 'online' || (stats.calculations?.checks?.failed === 0);
        if (isOnline) return 'Server is operational';
        if (stats.serverStatus === 'offline') return 'Server is offline';
        return 'Connectivity issues detected';
      })(),
      icon: Server,
      valueColor: getStatusColor('percentage', stats.serverUptime || stats.uptime)
    },
    {
      title: 'Database Performance',
      value: statsLoaded
        ? `${stats.databasePerformance?.percent ?? stats.systemHealth ?? 0}%`
        : '0%',
      changePercent: '',
      changeText: (() => {
        if (!statsLoaded) return 'Checking...';
        const dbLatency = stats.databasePerformance?.value || stats.avgResponseTime || 0;
        const dbPercent = stats.databasePerformance?.percent ?? stats.systemHealth ?? 0;
        if (dbPercent >= 95 || dbLatency <= 100) return 'Excellent';
        if (dbPercent >= 85 || dbLatency <= 300) return 'Good';
        if (dbPercent >= 70 || dbLatency <= 700) return 'Fair';
        return 'Poor';
      })(),
      icon: Database,
      valueColor: getStatusColor('percentage', stats.databasePerformance?.percent ?? stats.systemHealth),
      changeColor: getStatusColor('percentage', stats.databasePerformance?.percent ?? stats.systemHealth)
    }
  ], [stats, statsLoaded, user]);

  // Initialize Top Contributors Doughnut Chart
  useEffect(() => {
    let isMounted = true;
    
    const loadTopContributorsChart = () => {
      if (!isMounted || !topContributorsChartRef.current) return;
      
      const ctx = topContributorsChartRef.current.getContext('2d');
      if (!ctx) return;
      
      if (topContributorsChartInstanceRef.current) topContributorsChartInstanceRef.current.destroy();
      
      const { labels, data, colors } = topContributorsData;
      if (labels.length === 0) return;
      
      topContributorsChartInstanceRef.current = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: colors,
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 6
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: {
              position: 'right',
              labels: {
                boxWidth: 12,
                padding: 8,
                font: { size: 11 },
                color: '#4b5563',
                generateLabels: (chart) => {
                  const data = chart.data;
                  if (data.labels.length && data.datasets.length) {
                    return data.labels.map((label, i) => {
                      const dataset = data.datasets[0];
                      const value = dataset.data[i];
                      return {
                        text: `${label} (${value})`,
                        fillStyle: dataset.backgroundColor[i],
                        hidden: false,
                        index: i
                      };
                    });
                  }
                  return [];
                }
              }
            },
            tooltip: {
              backgroundColor: 'rgba(28, 31, 78, 0.94)',
              padding: 10,
              cornerRadius: 6,
              displayColors: false,
              callbacks: {
                label: (context) => {
                  const label = context.label || '';
                  const value = context.parsed || 0;
                  const total = context.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = ((value / total) * 100).toFixed(0);
                  return `${label}: ${value} docs (${percentage}%)`;
                }
              }
            }
          }
        }
      });
    };
    
    loadTopContributorsChart();
    return () => {
      isMounted = false;
      if (topContributorsChartInstanceRef.current) topContributorsChartInstanceRef.current.destroy();
    };
  }, [topContributorsData]);

  // Initialize User Location Vertical Bar Chart
  useEffect(() => {
    let isMounted = true;

    const loadUserLocationChart = () => {
      if (!isMounted || !userLocationChartRef.current) return;

      const ctx = userLocationChartRef.current.getContext('2d');
      if (!ctx) return;

      if (userLocationChartInstanceRef.current) userLocationChartInstanceRef.current.destroy();

      const { labels, data } = userLocationData;
      if (labels.length === 0) return;

      userLocationChartInstanceRef.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: 'Users',
            data: data,
            backgroundColor: (context) =>
              createPrimaryBarGradient(context.chart.ctx, context.chart.chartArea),
            hoverBackgroundColor: CHART_PRIMARY_END,
            borderColor: CHART_PRIMARY_START,
            borderWidth: 0,
            borderRadius: 6,
            barPercentage: 0.6
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: 'rgba(28, 31, 78, 0.94)',
              padding: 10,
              cornerRadius: 6,
              displayColors: false,
              callbacks: {
                label: (context) => {
                  const label = context.label || '';
                  const value = context.parsed?.y || 0;
                  return `${label}: ${value} users`;
                }
              }
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 11 }, maxRotation: 45, minRotation: 0 }
            },
            y: {
              beginAtZero: true,
              grid: { color: '#E5E7EB' },
              ticks: { stepSize: 1, major: { enabled: true }, callback: (val) => Math.round(val).toLocaleString() }
            }
          }
        }
      });
    };

    loadUserLocationChart();
    return () => {
      isMounted = false;
      if (userLocationChartInstanceRef.current) userLocationChartInstanceRef.current.destroy();
    };
  }, [userLocationData, userLocationFilter]);

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="main-content">
        {/* ===== PINAKA TOP GENERAL REPORT CARD ===== */}
        {(() => {
          const totalUsers    = statsLoaded ? (stats.totalUsers || 0) : 0;
          const activeUsers   = statsLoaded ? (stats.activeUsers || 0) : 0;
          const totalDocs     = statsLoaded ? (stats.totalDocuments || 0) : 0;
          const secAlerts     = statsLoaded ? (stats.securityAlerts || 0) : 0;
          const uptime        = statsLoaded ? (stats.serverUptime || stats.uptime || '0%') : '0%';
          const dbPct         = statsLoaded ? (stats.databasePerformance?.percent ?? stats.systemHealth ?? 0) : 0;
          const totalConsult  = allDocuments.reduce((s, d) =>
            s + (d.consultation?.analysisConsultation || 0) + (d.consultation?.generationConsultation || 0), 0);
          const newUsersThisMonth = (() => {
            const now = new Date(); const start = new Date(now.getFullYear(), now.getMonth(), 1);
            return (users || []).filter(u => u.createdAtMs && u.createdAtMs >= start.getTime()).length;
          })();
          const uniqueProvinces = new Set(allDocuments.map(d => d.location?.province || d.province).filter(Boolean)).size;
          // --- GEOGRAPHIC SOURCES breakdown ---
          const geoLevels = (() => {
            const counts = { Provincial: 0, Municipal: 0, Barangay: 0, National: 0, Other: 0 };
            allDocuments.forEach(d => {
              const lvl = String(d.legalLevel || '').trim();
              if (counts[lvl] !== undefined) counts[lvl]++;
              else counts.Other++;
            });
            return [
              { name: 'Provincial', count: counts.Provincial, color: '#818cf8' },
              { name: 'Municipal',  count: counts.Municipal,  color: '#34d399' },
              { name: 'Barangay',   count: counts.Barangay,   color: '#fbbf24' },
              { name: 'National',   count: counts.National,   color: '#60a5fa' },
            ].filter(r => r.count > 0);
          })();
          const geoMax = Math.max(1, ...geoLevels.map(r => r.count));

          const topGeoProvinces = (() => {
            const map = {};
            allDocuments.forEach(d => {
              const p = String(d.location?.province || d.province || '').trim();
              if (p) map[p] = (map[p] || 0) + 1;
            });
            return Object.entries(map).sort((a,b) => b[1]-a[1]).slice(0,4)
              .map(([name, count]) => ({ name, count }));
          })();
          const geoProvMax = Math.max(1, ...topGeoProvinces.map(r => r.count));

          // --- USERS BY LOCATION breakdown ---
          const topUserProvinces = (() => {
            const map = {};
            (users || []).forEach(u => {
              const p = String(u.location?.province || '').trim();
              if (p) map[p] = (map[p] || 0) + 1;
            });
            return Object.entries(map).sort((a,b) => b[1]-a[1]).slice(0,4)
              .map(([name, count]) => ({ name, count }));
          })();
          const userProvMax = Math.max(1, ...topUserProvinces.map(r => r.count));

          const totalUserRegions = new Set((users||[]).map(u => u.location?.province).filter(Boolean)).size;
          const uniqueMunicipalities = new Set((users||[]).map(u => u.location?.municipality).filter(Boolean)).size;
          const uniqueBarangays = new Set((users||[]).map(u => u.location?.barangay).filter(Boolean)).size;


          const uptimePct = parseFloat(String(uptime).replace('%','')) || 0;
          const uptimeClass = uptimePct >= 99 ? 'pinaka-status-good' : uptimePct >= 95 ? 'pinaka-status-mid' : 'pinaka-status-bad';
          const dbClass     = dbPct     >= 95 ? 'pinaka-status-good' : dbPct     >= 70 ? 'pinaka-status-mid' : 'pinaka-status-bad';
          const secClass    = secAlerts === 0  ? 'pinaka-status-good' : secAlerts <= 2 ? 'pinaka-status-mid' : 'pinaka-status-bad';


          const now = new Date();
          const dateStr = now.toLocaleDateString('en-US', { weekday:'short', year:'numeric', month:'short', day:'numeric' });

          return (
            <div className="pinaka-report-card">
              {/* Header */}
              <div className="pinaka-header">
                <div className="pinaka-header-left">
                  <div>
                    <h1 className="pinaka-title">Platform Overview</h1>
                    <p className="pinaka-subtitle">General report card — summarizing all platform metrics</p>
                  </div>
                </div>
                <span className="pinaka-timestamp">{dateStr}</span>
              </div>

              <div className="pinaka-divider" />

              {/* Metrics Grid */}
              <div className="pinaka-grid">
                <div className="pinaka-item">
                  <span className="pinaka-item-label">Total Users</span>
                  <span className="pinaka-item-value">{totalUsers.toLocaleString()}</span>
                  <span className="pinaka-item-desc">Registered community accounts</span>
                </div>
                <div className="pinaka-item">
                  <span className="pinaka-item-label">Active Users</span>
                  <span className="pinaka-item-value">{activeUsers.toLocaleString()}</span>
                  <span className="pinaka-item-desc">Interactive member sessions</span>
                </div>
                <div className="pinaka-item">
                  <span className="pinaka-item-label">Content Items</span>
                  <span className="pinaka-item-value">{totalDocs.toLocaleString()}</span>
                  <span className="pinaka-item-desc">Verified legal document entries</span>
                </div>
                <div className="pinaka-item">
                  <span className="pinaka-item-label">Security Alert</span>
                  <span className="pinaka-item-value">{secAlerts}</span>
                  <span className={`pinaka-item-status ${secClass}`}>
                    {secAlerts === 0 ? '✓ All Clear' : `⚠ ${secAlerts} Alert${secAlerts > 1 ? 's' : ''}`}
                  </span>
                </div>
                <div className="pinaka-item">
                  <span className="pinaka-item-label">Server Uptime</span>
                  <span className="pinaka-item-value">{uptime}</span>
                  <span className={`pinaka-item-status ${uptimeClass}`}>
                    {(() => {
                      if (!statsLoaded) return 'Checking...';
                      const isOnline = stats.serverStatus === 'online' || (stats.calculations?.checks?.failed === 0);
                      if (isOnline) return 'Server is operational';
                      if (stats.serverStatus === 'offline') return 'Server is offline';
                      return 'Connectivity issues detected';
                    })()}
                  </span>
                </div>
                <div className="pinaka-item">
                  <span className="pinaka-item-label">DB Performance</span>
                  <span className="pinaka-item-value">{dbPct}%</span>
                  <span className={`pinaka-item-status ${dbClass}`}>
                    {dbPct >= 95 ? 'Excellent' : dbPct >= 85 ? 'Good' : dbPct >= 70 ? 'Fair' : 'Poor'}
                  </span>
                </div>
              </div>

              {/* Summary bar — Simplified to include all key highlights */}
              <div className="pinaka-summary-bar">
                <div className="pinaka-summary-item">
                  <span className="pinaka-summary-dot" />
                  Most Consulted: <strong>{totalConsult.toLocaleString()} total queries</strong>
                </div>
                <div className="pinaka-summary-item">
                  <span className="pinaka-summary-dot" />
                  User Growth: <strong>+{newUsersThisMonth} new this month</strong>
                </div>
                <div className="pinaka-summary-item">
                  <span className="pinaka-summary-dot" />
                  Doc Uploads: <strong>{totalDocs.toLocaleString()} total entries</strong>
                </div>

              </div>


            </div>
          );
        })()}

        {/* 6 Statistics Cards */}
        <div className="stats-grid">

          {statistics.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className="stat-header">
                <span className="stat-title">{stat.title}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {stat.statusIndicator && (
                    <div className={`status-dot ${stat.statusIndicator}`} title={`Status: ${stat.statusIndicator}`}></div>
                  )}
                  <Icon icon={stat.icon} size={24} className="stat-icon" />
                </div>
              </div>
              <div className="stat-value" style={{ color: stat.valueColor }}>{stat.value}</div>
              <div className="stat-change">
                {stat.changePercent && (
                  <span className="stat-change-percent" style={{ color: stat.changeColor }}>
                    {stat.changePercent}
                  </span>
                )}
                <span className="stat-change-text">{stat.changeText}</span>
              </div>
            </div>
          ))}
        </div>


        {/* ROW 1: MOST CONSULTED ORDINANCES + TOP CONTRIBUTORS - Side by Side */}
        <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', alignItems: 'stretch' }}>

          {/* MOST CONSULTED ORDINANCES CHART - BAR CHART */}
          <div className="chart-card" style={{ flex: 1, minWidth: 0, marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Card header with filter toggle */}
            {(() => {
              const hasActive = ordinanceActivityFilter !== 'both' || ordinanceLevelFilter !== 'all' || ordinanceProvinceFilter !== 'all' || ordinanceMunicipalityFilter !== 'all' || ordinanceBarangayFilter !== 'all';
              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px', position: 'relative' }}>
                  <div>
                    <h2 className="chart-title">Most Consulted Documents</h2>
                    <p className="chart-subtitle">User access frequency by law type</p>
                  </div>

                  {/* Right side: filter button + view all button */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <button
                      className="chart-view-all-btn"
                      onClick={() => setShowConsultedDocsModal(true)}
                      title="View all documents"
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'transparent',
                        color: CHART_PRIMARY_START,
                        border: `1px solid ${CHART_PRIMARY_START}`,
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => { e.target.style.backgroundColor = CHART_PRIMARY_START; e.target.style.color = 'white'; }}
                      onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = CHART_PRIMARY_START; }}
                    >
                      View All
                    </button>
                    <div style={{ position: 'relative' }}>
                    <button
                      className={`chart-filter-btn${showOrdinanceFilters || hasActive ? ' active' : ''}`}
                      onClick={() => {
                        if (!showOrdinanceFilters) {
                          setTempOrdinanceFilters({ activity: ordinanceActivityFilter, level: ordinanceLevelFilter, province: ordinanceProvinceFilter, municipality: ordinanceMunicipalityFilter, barangay: ordinanceBarangayFilter });
                        }
                        setShowOrdinanceFilters(f => !f);
                      }}
                      title="Filter"
                    >
                      <Icon icon={MoreVertical} size={20} />
                    </button>

                    {showOrdinanceFilters && (
                      <>
                        <div className="user-filter-modal-overlay" onClick={() => setShowOrdinanceFilters(false)} />
                        <div className="user-filter-modal-content" style={{ right: 0, left: 'auto', width: '280px' }}>
                          <div className="user-filter-modal-header">
                            <span className="user-filter-modal-title">Filter Chart</span>
                            <button className="user-filter-modal-close-icon" onClick={() => setShowOrdinanceFilters(false)}>
                              <Icon icon={X} size={16} />
                            </button>
                          </div>

                          <div className="user-filter-body">
                            {/* Activity */}
                            <div className="user-filter-section">
                              <label className="user-filter-section-label">Activity</label>
                              <div className="user-filter-chips">
                                {[{v:'both',l:'Both'},{v:'generation',l:'Generation'},{v:'analysis',l:'Analysis'}].map(o => (
                                  <button key={o.v} className={`user-filter-chip${tempOrdinanceFilters.activity===o.v?' active':''}`} onClick={() => setTempOrdinanceFilters(f=>({...f, activity: o.v}))}>{o.l}</button>
                                ))}
                              </div>
                            </div>

                            {/* Law Level */}
                            {(user?.role === 'system_admin' || user?.adminaccessLevel === 'municipal' || user?.adminaccessLevel === 'none' || !user?.adminaccessLevel) && (
                              <div className="user-filter-section">
                                <label className="user-filter-section-label">Law Level</label>
                                <div className="user-filter-chips">
                                  {[
                                    {v:'all',l:'All'},
                                    ...((user?.role === 'system_admin' || !user?.adminaccessLevel || user?.adminaccessLevel === 'none') ? [{v:'national',l:'National'}] : []),
                                    {v:'provincial',l:'Provincial'},
                                    {v:'municipal',l:'Municipal'},
                                    {v:'barangay',l:'Barangay'},
                                  ].map(o => (
                                    <button key={o.v} className={`user-filter-chip${tempOrdinanceFilters.level===o.v?' active':''}`} onClick={() => setTempOrdinanceFilters(f=>({...f, level: o.v}))}>{o.l}</button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Location */}
                            {(user?.role === 'system_admin' || user?.adminaccessLevel === 'none' || !user?.adminaccessLevel || user?.adminaccessLevel === 'municipal' || user?.adminaccessLevel === 'barangay') && (
                              <div className="user-filter-section">
                                <label className="user-filter-section-label">Location</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {(user?.role === 'system_admin' || user?.adminaccessLevel === 'none' || !user?.adminaccessLevel) && (
                                    <select className="filter-panel-select" value={tempOrdinanceFilters.province} onChange={e => setTempOrdinanceFilters(f=>({...f, province: e.target.value, municipality: 'all', barangay: 'all'}))}>
                                      <option value="all">All Provinces</option>
                                      {availableProvinces.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                  )}
                                  {(user?.role === 'system_admin' || !user?.adminaccessLevel || user?.adminaccessLevel === 'none') && (
                                    <select className="filter-panel-select" value={tempOrdinanceFilters.municipality} onChange={e => setTempOrdinanceFilters(f=>({...f, municipality: e.target.value, barangay: 'all'}))}>
                                      <option value="all">All Municipalities</option>
                                      {availableOrdinanceMunicipalities.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                  )}
                                  {(user?.role === 'system_admin' || user?.adminaccessLevel === 'municipal' || user?.adminaccessLevel === 'none') && (
                                    <select className="filter-panel-select" value={tempOrdinanceFilters.barangay} onChange={e => setTempOrdinanceFilters(f=>({...f, barangay: e.target.value}))}>
                                      <option value="all">All Barangays</option>
                                      {availableOrdinanceBarangays.map(b => <option key={b} value={b}>{b.startsWith('Barangay') ? b : `Barangay ${b}`}</option>)}
                                    </select>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="user-filter-modal-footer">
                            <button className="user-filter-reset-btn" onClick={() => setTempOrdinanceFilters({ activity: 'both', level: 'all', province: 'all', municipality: 'all', barangay: 'all' })}>Reset</button>
                            <button className="user-filter-apply-btn" onClick={() => {
                              setOrdinanceActivityFilter(tempOrdinanceFilters.activity);
                              setOrdinanceLevelFilter(tempOrdinanceFilters.level);
                              setOrdinanceProvinceFilter(tempOrdinanceFilters.province);
                              setOrdinanceMunicipalityFilter(tempOrdinanceFilters.municipality);
                              setOrdinanceBarangayFilter(tempOrdinanceFilters.barangay);
                              setShowOrdinanceFilters(false);
                            }}>Apply</button>
                          </div>
                        </div>
                      </>
                    )}
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="chart-container" style={{ flex: 1, minHeight: '260px' }}>
              <canvas ref={ordinanceChartRef}></canvas>
            </div>
          </div>

          {/* TOP CONTRIBUTORS CHART - DOUGHNUT CHART */}
          <div className="chart-card" style={{ flex: 1, minWidth: 0, marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px', position: 'relative' }}>
              <div>
                <h2 className="chart-title">Geographic Sources</h2>
                <p className="chart-subtitle">Document uploads by geographic location</p>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <span style={{ fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap', color: CHART_PRIMARY_START }}>Total Documents: {topContributorsData.total?.toLocaleString() || 0}</span>
                <div style={{ position: 'relative' }}>
                {(() => {
                  const hasActive = topContributorsFilter !== 'all' || topContributorsProvinceFilter !== 'all' || topContributorsMunicipalityFilter !== 'all' || topContributorsBarangayFilter !== 'all';
                  return (
                    <>
                      <button
                        className={`chart-filter-btn${showTopContributorsFilters || hasActive ? ' active' : ''}`}
                        onClick={() => {
                          if (!showTopContributorsFilters) {
                            setTempTopContributorsFilters({ 
                              level: topContributorsFilter, 
                              province: topContributorsProvinceFilter, 
                              municipality: topContributorsMunicipalityFilter, 
                              barangay: topContributorsBarangayFilter 
                            });
                          }
                          setShowTopContributorsFilters(f => !f);
                        }}
                        title="Filter"
                      >
                        <Icon icon={MoreVertical} size={20} />
                      </button>

                      {showTopContributorsFilters && (
                        <>
                          <div className="user-filter-modal-overlay" onClick={() => setShowTopContributorsFilters(false)} />
                          <div className="user-filter-modal-content" style={{ right: 0, left: 'auto', width: '280px' }}>
                            <div className="user-filter-modal-header">
                              <span className="user-filter-modal-title">Filter Contributors</span>
                              <button className="user-filter-modal-close-icon" onClick={() => setShowTopContributorsFilters(false)}>
                                <Icon icon={X} size={16} />
                              </button>
                            </div>

                            <div className="user-filter-modal-body">
                              <div className="user-filter-section">
                                <label className="user-filter-section-label">Legal Level</label>
                                <div className="user-filter-chips">
                                  {[
                                    {v:'all',l:'All Levels'},
                                    {v:'provincial',l:'Provincial'},
                                    {v:'municipal',l:'Municipal'},
                                    {v:'barangay',l:'Barangay'}
                                  ].map(o => (
                                    <button 
                                      key={o.v} 
                                      className={`user-filter-chip${tempTopContributorsFilters.level===o.v?' active':''}`} 
                                      onClick={() => setTempTopContributorsFilters(f => ({...f, level: o.v}))}
                                    >
                                      {o.l}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <div className="user-filter-section">
                                <label className="user-filter-section-label">Location Filter</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  <select 
                                    className="filter-panel-select" 
                                    value={tempTopContributorsFilters.province} 
                                    onChange={e => setTempTopContributorsFilters(f => ({...f, province: e.target.value, municipality: 'all', barangay: 'all'}))}
                                  >
                                    <option value="all">All Provinces</option>
                                    {availableProvinces.map(p => <option key={p} value={p}>{p}</option>)}
                                  </select>
                                  <select 
                                    className="filter-panel-select" 
                                    value={tempTopContributorsFilters.municipality} 
                                    onChange={e => setTempTopContributorsFilters(f => ({...f, municipality: e.target.value, barangay: 'all'}))}
                                  >
                                    <option value="all">All Municipalities</option>
                                    {availableUserMunicipalities.map(m => (tempTopContributorsFilters.province === 'all' || availableProvinces.includes(tempTopContributorsFilters.province)) ? <option key={m} value={m}>{m}</option> : null)}
                                  </select>
                                  <select 
                                    className="filter-panel-select" 
                                    value={tempTopContributorsFilters.barangay} 
                                    onChange={e => setTempTopContributorsFilters(f => ({...f, barangay: e.target.value}))}
                                  >
                                    <option value="all">All Barangays</option>
                                    {availableUserBarangays.map(b => (
                                      <option key={b} value={b}>{b.startsWith('Barangay') ? b : `Barangay ${b}`}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>

                            <div className="user-filter-modal-footer">
                              <button 
                                className="user-filter-reset-btn" 
                                onClick={() => setTempTopContributorsFilters({ level: 'all', province: 'all', municipality: 'all', barangay: 'all' })}
                              >
                                Reset
                              </button>
                              <button 
                                className="user-filter-apply-btn" 
                                onClick={() => {
                                  setTopContributorsFilter(tempTopContributorsFilters.level);
                                  setTopContributorsProvinceFilter(tempTopContributorsFilters.province);
                                  setTopContributorsMunicipalityFilter(tempTopContributorsFilters.municipality);
                                  setTopContributorsBarangayFilter(tempTopContributorsFilters.barangay);
                                  setShowTopContributorsFilters(false);
                                }}
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
                </div>
              </div>
            </div>
            <div className="chart-container" style={{ flex: 1, minHeight: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <canvas ref={topContributorsChartRef} style={{ maxHeight: '240px' }}></canvas>
            </div>
          </div>

        </div>

        {/* ROW 2: USER GROWTH TREND + USERS BY LOCATION - Side by Side */}
        <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', alignItems: 'stretch' }}>

          {/* USER GROWTH TREND CHART - LINE CHART */}
          <div className="chart-card" style={{ flex: 1, minWidth: 0, marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px', position: 'relative' }}>
              <div>
                <h2 className="chart-title">User Growth Trend</h2>
                <p className="chart-subtitle">
                  Cumulative community registration ({
                    userGrowthRange === '7' ? 'Last 1 week' : 
                    userGrowthRange === '30' ? 'Last 1 month' : 
                    userGrowthRange === '90' ? 'Last 3 months' : 
                    userGrowthRange === '180' ? 'Last 6 months' : 
                    userGrowthRange === '365' ? 'Last 1 year' : 
                    'Overall'
                  })
                </p>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <div style={{ color: CHART_PRIMARY_START, flexShrink: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                    {userGrowthData.dataset.length > 0 ? (userGrowthData.dataset[userGrowthData.dataset.length - 1] - userGrowthData.dataset[0]).toLocaleString() : 0} new users registered {
                      userGrowthRange === '7' ? 'this week' : 
                      userGrowthRange === '30' ? 'this month' : 
                      userGrowthRange === '90' ? 'in the last 3 months' : 
                      userGrowthRange === '180' ? 'in the last 6 months' : 
                      userGrowthRange === '365' ? 'this year' : 
                      'overall'
                    }
                  </span>
                </div>

                <div style={{ position: 'relative' }}>
                  <button
                    className={`chart-filter-btn${showUserGrowthFilters ? ' active' : ''}`}
                    onClick={() => {
                      if (!showUserGrowthFilters) {
                        setTempUserGrowthRange(userGrowthRange);
                      }
                      setShowUserGrowthFilters(f => !f);
                    }}
                    title="Filter"
                  >
                    <Icon icon={MoreVertical} size={20} />
                  </button>

                  {showUserGrowthFilters && (
                    <>
                      <div className="user-filter-modal-overlay" onClick={() => setShowUserGrowthFilters(false)} />
                      <div className="user-filter-modal-content" style={{ right: 0, left: 'auto', width: '240px' }}>
                        <div className="user-filter-modal-header">
                          <span className="user-filter-modal-title">Filter Trend</span>
                          <button className="user-filter-modal-close-icon" onClick={() => setShowUserGrowthFilters(false)}>
                            <Icon icon={X} size={16} />
                          </button>
                        </div>

                        <div className="user-filter-modal-body">
                          <div className="user-filter-section">
                            <label className="user-filter-section-label">Time Range</label>
                            <div className="user-filter-chips" style={{ gridTemplateColumns: '1fr 1fr' }}>
                              {[
                                {v:'7',l:'1 Week'},
                                {v:'30',l:'1 Month'},
                                {v:'90',l:'3 Months'},
                                {v:'180',l:'6 Months'},
                                {v:'365',l:'1 Year'},
                                {v:'overall',l:'Overall'}
                              ].map(o => (
                                <button
                                  key={o.v}
                                  className={`user-filter-chip${tempUserGrowthRange===o.v?' active':''}`}
                                  onClick={() => setTempUserGrowthRange(o.v)}
                                  style={{ justifyContent: 'center', padding: '10px 12px' }}
                                >
                                  {o.l}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="user-filter-modal-footer">
                          <button 
                            className="user-filter-reset-btn" 
                            onClick={() => setTempUserGrowthRange('30')}
                          >
                            Reset
                          </button>
                          <button 
                            className="user-filter-apply-btn" 
                            onClick={() => {
                              setUserGrowthRange(tempUserGrowthRange);
                              setShowUserGrowthFilters(false);
                            }}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="chart-container" style={{ flex: 1, minHeight: '260px' }}>
              <canvas ref={userGrowthChartRef}></canvas>
            </div>
          </div>

          {/* USERS BY LOCATION - BAR CHART */}
          <div className="chart-card" style={{ flex: 1, minWidth: 0, marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px', position: 'relative' }}>
              <div>
                <h2 className="chart-title">Users by Location</h2>
                <p className="chart-subtitle">User distribution by geographic location</p>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <span style={{ fontWeight: 600, fontSize: '0.95rem', whiteSpace: 'nowrap', color: CHART_PRIMARY_START }}>Total Users: {userLocationData.total?.toLocaleString() || 0}</span>
                <div style={{ position: 'relative' }}>
                {(() => {
                  const hasActive = userLocationFilter !== 'province' || userProvinceFilter !== 'all' || userMunicipalityFilter !== 'all' || userBarangayFilter !== 'all';
                  return (
                    <>
                      <button
                        className={`chart-filter-btn${showUserLocationFilters || hasActive ? ' active' : ''}`}
                        onClick={() => {
                          if (!showUserLocationFilters) {
                            setTempUserLocationFilters({ 
                              groupBy: userLocationFilter, 
                              province: userProvinceFilter, 
                              municipality: userMunicipalityFilter, 
                              barangay: userBarangayFilter 
                            });
                          }
                          setShowUserLocationFilters(f => !f);
                        }}
                        title="Filter"
                      >
                        <Icon icon={MoreVertical} size={20} />
                      </button>

                      {showUserLocationFilters && (
                        <>
                          <div className="user-filter-modal-overlay" onClick={() => setShowUserLocationFilters(false)} />
                          <div className="user-filter-modal-content" style={{ right: 0, left: 'auto', width: '280px' }}>
                            <div className="user-filter-modal-header">
                              <span className="user-filter-modal-title">Filter Location</span>
                              <button className="user-filter-modal-close-icon" onClick={() => setShowUserLocationFilters(false)}>
                                <Icon icon={X} size={16} />
                              </button>
                            </div>

                            <div className="user-filter-modal-body">
                              <div className="user-filter-section">
                                <label className="user-filter-section-label">Group By</label>
                                <div className="user-filter-chips">
                                  {[
                                    {v:'province',l:'Province'},
                                    {v:'municipality',l:'Municipality'},
                                    {v:'barangay',l:'Barangay'}
                                  ].map(o => (
                                    <button 
                                      key={o.v} 
                                      className={`user-filter-chip${tempUserLocationFilters.groupBy===o.v?' active':''}`} 
                                      onClick={() => setTempUserLocationFilters(f => ({...f, groupBy: o.v}))}
                                    >
                                      {o.l}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <div className="user-filter-section">
                                <label className="user-filter-section-label">Location Filter</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  <select 
                                    className="filter-panel-select" 
                                    value={tempUserLocationFilters.province} 
                                    onChange={e => setTempUserLocationFilters(f => ({...f, province: e.target.value, municipality: 'all', barangay: 'all'}))}
                                  >
                                    <option value="all">All Provinces</option>
                                    {availableProvinces.map(p => <option key={p} value={p}>{p}</option>)}
                                  </select>
                                  <select 
                                    className="filter-panel-select" 
                                    value={tempUserLocationFilters.municipality} 
                                    onChange={e => setTempUserLocationFilters(f => ({...f, municipality: e.target.value, barangay: 'all'}))}
                                  >
                                    <option value="all">All Municipalities</option>
                                    {availableUserMunicipalities.map(m => <option key={m} value={m}>{m}</option>)}
                                  </select>
                                  <select 
                                    className="filter-panel-select" 
                                    value={tempUserLocationFilters.barangay} 
                                    onChange={e => setTempUserLocationFilters(f => ({...f, barangay: e.target.value}))}
                                  >
                                    <option value="all">All Barangays</option>
                                    {availableUserBarangays.map(b => (
                                      <option key={b} value={b}>{b.startsWith('Barangay') ? b : `Barangay ${b}`}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>

                            <div className="user-filter-modal-footer">
                              <button 
                                className="user-filter-reset-btn" 
                                onClick={() => setTempUserLocationFilters({ groupBy: 'province', province: 'all', municipality: 'all', barangay: 'all' })}
                              >
                                Reset
                              </button>
                              <button 
                                className="user-filter-apply-btn" 
                                onClick={() => {
                                  setUserLocationFilter(tempUserLocationFilters.groupBy);
                                  setUserProvinceFilter(tempUserLocationFilters.province);
                                  setUserMunicipalityFilter(tempUserLocationFilters.municipality);
                                  setUserBarangayFilter(tempUserLocationFilters.barangay);
                                  setShowUserLocationFilters(false);
                                }}
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              </div>
            </div>
            <div className="chart-container" style={{ flex: 1, minHeight: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <canvas ref={userLocationChartRef} style={{ maxHeight: '240px' }}></canvas>
            </div>
          </div>

        </div>


        {/* MODAL: Full View - Most Consulted Documents */}
        {renderConsultedDocsModal && (
          <div
            className={`consulted-docs-modal-overlay modal-transition-${showConsultedDocsModal ? 'enter' : 'exit'}`}
            onClick={() => setShowConsultedDocsModal(false)}
          >
            <div 
              className="consulted-docs-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="consulted-docs-modal-header">
                <div>
                  <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#1c1d1e' }}>All Most Consulted Documents</h2>
                  <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666', fontWeight: 400 }}>Complete list of documents ranked by user access frequency</p>
                </div>
                <button 
                  className="consulted-docs-modal-close-btn"
                  onClick={() => setShowConsultedDocsModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#999',
                    padding: '0',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Stats Bar */}
              <div className="consulted-docs-modal-stats" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '16px',
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, marginBottom: '4px' }}>Total Documents</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: CHART_PRIMARY_START }}>{allConsultedDocuments.length}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, marginBottom: '4px' }}>Total Consultations</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: CHART_PRIMARY_START }}>{filteredConsultedDocuments.reduce((s, d) => s + d.consultations, 0).toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, marginBottom: '4px' }}>Avg Consultations</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: CHART_PRIMARY_START }}>
                    {filteredConsultedDocuments.length > 0 ? Math.round(filteredConsultedDocuments.reduce((s, d) => s + d.consultations, 0) / filteredConsultedDocuments.length) : 0}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, marginBottom: '4px' }}>Most Consulted</div>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: CHART_PRIMARY_START }}>
                    {filteredConsultedDocuments.length > 0 ? filteredConsultedDocuments[0].consultations.toLocaleString() : 0}
                  </div>
                </div>
              </div>

              {/* Search Bar */}
              <div className="consulted-docs-modal-controls" style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                alignItems: 'center'
              }}>
                <input
                  type="text"
                  placeholder="Search documents by title, location, or level..."
                  value={consultedDocsSearchQuery}
                  onChange={(e) => setConsultedDocsSearchQuery(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    fontSize: '13px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '6px',
                    fontFamily: '"Inter", sans-serif',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = CHART_PRIMARY_START}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
                {consultedDocsSearchQuery && (
                  <button
                    onClick={() => setConsultedDocsSearchQuery('')}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#666'
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Table */}
              <div className="consulted-docs-modal-table-wrapper" style={{
                flex: 1,
                overflowY: 'auto',
                borderRadius: '8px',
                border: '1px solid #e0e0e0'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '13px'
                }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa' }}>
                    <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#1c1d1e' }}>Document Title</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#1c1d1e', width: '100px' }}>Consultations</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#1c1d1e', width: '80px' }}>Analysis</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600, color: '#1c1d1e', width: '80px' }}>Generation</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#1c1d1e', width: '120px' }}>Level</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, color: '#1c1d1e', width: '120px' }}>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredConsultedDocuments.length > 0 ? (
                      filteredConsultedDocuments.map((doc, idx) => (
                        <tr 
                          key={doc.id || idx}
                          style={{ 
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8f9fa',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#ffffff' : '#f8f9fa'}
                        >
                          <td style={{ padding: '12px', textAlign: 'left', color: '#1c1d1e', fontWeight: 500 }}>
                            <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.title}>
                              {doc.title}
                            </div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: CHART_PRIMARY_START, fontWeight: 700 }}>
                            {doc.consultations.toLocaleString()}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                            {doc.analysisConsultation}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                            {doc.generationConsultation}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'left', color: '#666', fontSize: '12px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              backgroundColor: '#f0f0f0',
                              borderRadius: '4px'
                            }}>
                              {doc.legalLevel}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'left', color: '#666', fontSize: '12px' }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px' }} title={doc.legalLevel === 'national' || doc.legalLevel === 'National' ? 'Philippines' : `${doc.municipality}, ${doc.province}`}>
                              {doc.legalLevel === 'national' || doc.legalLevel === 'National' ? 'Philippines' : (doc.municipality && doc.province ? `${doc.municipality}, ${doc.province}` : doc.province)}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                          No documents found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}


        {/* Document Upload Calendar - GitHub Style Heatmap */}
        <div style={{ marginBottom: '24px' }}>
          <DocumentUploadCalendar documents={allDocuments} loading={!documentsLoaded} user={user} />
        </div>
      </main>
    </div>
  );
};

export default SA_Overview1;
